import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash2, Plus, Building2 } from "lucide-react";
import { toast } from "sonner";

const empty = { code: "", name: "", head_user_id: "", cost_centre_id: "", description: "", is_active: true };

export default function DepartmentsTab() {
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [ccs, setCcs] = useState([]);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setItems((await api.get("/departments")).data || []);
    try { setUsers((await api.get("/company/users")).data || []); } catch { setUsers([]); }
    try { setCcs((await api.get("/dimensions")).data || []); } catch { setCcs([]); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.code.trim() || !form.name.trim()) return toast.error("Code + Name required");
    const payload = {
      ...form,
      head_user_id: form.head_user_id || null,
      cost_centre_id: form.cost_centre_id || null,
    };
    try {
      if (editing) await api.put(`/departments/${editing}`, payload);
      else await api.post("/departments", payload);
      toast.success(editing ? "Updated" : "Department added");
      setForm(empty); setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this department?")) return;
    try { await api.delete(`/departments/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const startEdit = (d) => { setEditing(d.id); setForm({ ...empty, ...d, head_user_id: d.head_user_id || "", cost_centre_id: d.cost_centre_id || "" }); };
  const cancel    = () => { setEditing(null); setForm(empty); };

  return (
    <div className="space-y-4" data-testid="departments-tab">
      <div className="text-[10px] font-mono uppercase text-muted-foreground">// Departments · Sales · Purchase · Ops · HR · Finance</div>

      <Card className="rounded-sm p-4">
        <div className="text-sm font-semibold mb-3 flex items-center gap-2"><Building2 className="w-4 h-4 text-accent" />{editing ? "Edit department" : "Add department"}</div>
        <div className="grid md:grid-cols-5 gap-3">
          <div>
            <Label className="text-[10px] uppercase font-mono">Code *</Label>
            <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                   placeholder="SALES" className="rounded-sm mt-1 font-mono" data-testid="dept-code" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-[10px] uppercase font-mono">Name *</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                   placeholder="Sales & Marketing" className="rounded-sm mt-1" data-testid="dept-name" />
          </div>
          <div>
            <Label className="text-[10px] uppercase font-mono">Head</Label>
            <Select value={form.head_user_id || "__none__"} onValueChange={(v) => setForm({ ...form, head_user_id: v === "__none__" ? "" : v })}>
              <SelectTrigger className="rounded-sm mt-1" data-testid="dept-head"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">—</SelectItem>
                {users.map(u => <SelectItem key={u.id} value={u.id}>{u.full_name || u.email}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase font-mono">Dimension</Label>
            <Select value={form.cost_centre_id || "__none__"} onValueChange={(v) => setForm({ ...form, cost_centre_id: v === "__none__" ? "" : v })}>
              <SelectTrigger className="rounded-sm mt-1" data-testid="dept-cc"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">—</SelectItem>
                {ccs.map(cc => <SelectItem key={cc.id} value={cc.id}>{cc.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="mt-3">
          <Label className="text-[10px] uppercase font-mono">Description</Label>
          <Input value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })}
                 placeholder="What this department owns" className="rounded-sm mt-1" data-testid="dept-desc" />
        </div>
        <div className="mt-3 flex gap-2">
          <Button onClick={save} className="rounded-sm gap-1 bg-accent hover:bg-accent/90 text-white" data-testid="dept-save">
            <Plus className="w-3.5 h-3.5" /> {editing ? "Update" : "Add"}
          </Button>
          {editing && <Button variant="outline" onClick={cancel} className="rounded-sm" data-testid="dept-cancel">Cancel</Button>}
        </div>
      </Card>

      <Card className="rounded-sm overflow-hidden">
        <table className="w-full dense">
          <thead><tr><th>Code</th><th>Name</th><th>Head</th><th>Dimension</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan="6" className="text-center py-8 text-muted-foreground">No departments yet.</td></tr>}
            {items.map((d) => (
              <tr key={d.id} data-testid={`dept-row-${d.code}`} className={editing === d.id ? "bg-accent/5" : ""}>
                <td className="font-mono text-xs">{d.code}</td>
                <td>{d.name}</td>
                <td className="text-xs">{(users.find(u => u.id === d.head_user_id) || {}).full_name || "—"}</td>
                <td className="text-xs">{(ccs.find(c => c.id === d.cost_centre_id) || {}).name || "—"}</td>
                <td>{d.is_active ? <span className="text-emerald-700 text-xs font-mono">Active</span> : <span className="text-muted-foreground text-xs">Inactive</span>}</td>
                <td className="text-right space-x-2">
                  <button onClick={() => startEdit(d)} className="text-xs text-accent hover:underline" data-testid={`dept-edit-${d.code}`}>Edit</button>
                  <button onClick={() => del(d.id)} className="text-muted-foreground hover:text-destructive" data-testid={`dept-delete-${d.code}`}>
                    <Trash2 className="w-3.5 h-3.5 inline" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
